<template>
  <v-container class="page">
    <v-overlay :value="overlay">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>
    <v-card
      v-if="is_page_table"
      width="100%"
      style="border-radius:10px;"
      class="pa-3"
      v-show="!overlay"
    >
      <v-card-title class="title font-weight-light">
        Email Template Management
        <v-spacer />
        <v-text-field
          v-model="searchData"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        />
      </v-card-title>
      <v-card-text>
        <div class="d-flex">
          <v-btn
            color="#00ACDC"
            dark
            class="mb-2 ml-auto"
            @click="handleClickCreateNew"
          >
            Create New Email Template
          </v-btn>
        </div>
        <v-data-table
          :headers="headers"
          :items="email_templates"
          class="font-weight-light"
          :loading="loading_email"
          loading-text="Loading..."
          :footer-props="footerProps"
          @update:options="updateEmailTemplate"
          :server-items-length="email_templates_total"
          :options.sync="paginationOptions"
        >
          <template #[`item.no`]=" { item }">
            {{ ((paginationOptions.page - 1) * paginationOptions.itemsPerPage) + email_templates.indexOf(item) + 1 }}
          </template>
          <template #[`item.type`]="{ item }">
            {{ getTypeName(item.type) }}
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at, 'D MMM YYYY HH:mm') }}
          </template>
          <template #[`item.updated_at`]="{ item }">
            {{ formatDate(item.updated_at, 'D MMM YYYY HH:mm') }}
          </template>
          <template #[`item.action`]="{ item }">
            <v-icon
              small
              class="mr-2"
              color="green"
              @click="handleClickEdit(item)"
            >
              mdi-pencil
            </v-icon>
            <v-icon
              small
              color="red"
              @click="handleClickDelete(item)"
            >
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-card
      v-if="is_page_form"
      class="mx-auto"
      style="border-radius:10px;"
    >
      <v-card-title>{{ title }}</v-card-title>
      <v-card-text>
        <v-form
          ref="form"
          model="valid"
          lazy-validation
        >
          <v-row>
            <v-col>
              <v-card-subtitle class="px-0 pt-0">
                Subject
              </v-card-subtitle>
              <v-text-field
                v-model="subject"
                :rules="[(v) => !!v || 'Subject cannot be blank']"
                :counter="255"
                required
                outlined
                dense
              />
            </v-col>
            <v-col>
              <v-card-subtitle class="px-0 pt-0">
                Type
              </v-card-subtitle>
              <v-select
                v-model="type"
                :items="filter_email_template_types"
                item-text="value"
                item-value="key"
                :rules="[(v) => !!v || 'Please select at least one type']"
                required
                outlined
                dense
              />
            </v-col>
          </v-row>
          <v-card-subtitle class="px-0 pt-0">
            <span>Body</span>
            <v-btn
              class="float-right"
              @click="handleLoadDefault"
            >
              Load default template
            </v-btn>
          </v-card-subtitle>
          <tiptap-vuetify
            ref="tiptap"
            v-model="body"
            :extensions="extensions"
            :native-extensions="nativeExtensions"
            :toolbar-attributes="{ color: '#00ACDC' }"
            min-height="300"
            class="pt-1"
            placeholder="Start Typing Here"
          />
          <span
            v-if="bodyEmpty"
            class="red--text caption"
          >Body cannot be blank</span>
        </v-form>
      </v-card-text>
      <v-card-actions class="px-4 mb-3">
        <v-spacer />
        <v-btn
          @click="handleClickBack"
          type="submit"
          color="error"
        >
          Back
        </v-btn>
        <v-btn
          @click="handleClickSubmit"
          type="submit"
          color="success"
          :loading="loadingUpdate"
          :disabled="loadingUpdate"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog
      v-model="dialog"
      persistent
      max-width="600"
    >
      <v-card
        dark
        color="red"
      >
        <v-card-title>
          <span>
            <v-icon>mdi-alert-circle</v-icon> Are you sure to want to
            delete this email template?
          </span>
        </v-card-title>
        <v-card-text>
          You will delete this email template permanently.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="dialog = false"
          >
            No
          </v-btn>
          <v-btn
            text
            @click="handleConfirmDelete()"
            :loading="loadingUpdate"
            :disabled="loadingUpdate"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="replace"
      persistent
      max-width="600"
    >
      <v-card
        dark
        color="red"
      >
        <v-card-title>
          <span>
            <v-icon>mdi-alert-circle</v-icon> Are you sure to want to
            load default template?
          </span>
        </v-card-title>
        <v-card-text>
          Your current email template body is not empty, you will lose your current email template body.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="replace = false"
          >
            No
          </v-btn>
          <v-btn
            text
            @click="handleClickReplace()"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment';
import { Node } from 'tiptap';
import {
TiptapVuetify,
Heading,
Bold,
Italic,
Strike,
Underline,
Code,
Paragraph,
BulletList,
OrderedList,
ListItem,
Link,
Blockquote,
HardBreak,
HorizontalRule,
History,
Image,
Table,
TableCell,
TableHeader,
TableRow
} from 'tiptap-vuetify'

class ContentPlaceholder extends Node {
  get name() {
    return 'content_placeholder';
  }

  get schema() {
    return {
      attrs: { label: {} },
      inline: true,
      group: 'inline',
      atom: true,
      selectable: true,
      toDOM: (node) => [
        'span',
        {
          class: 'content-placeholder',
          'data-label': node.attrs.label,
          contenteditable: 'false',
        },
        node.attrs.label,
      ],
      parseDOM: [{
        tag: 'span.content-placeholder[data-label]',
        getAttrs: (dom) => ({ label: dom.getAttribute('data-label') }),
      }],
    };
  }
}

export default {
  components: { TiptapVuetify },
  data: () => ({
    page: 'table',
    nativeExtensions: [new ContentPlaceholder()],
    extensions: [
      History,
      Table,
      TableCell,
      TableHeader,
      TableRow,
      Blockquote,
      Link,
      Underline,
      Strike,
      Italic,
      ListItem,
      BulletList,
      OrderedList,
      Image,
      [
        Heading,
        {
          options: {
            levels: [1, 2, 3]
          }
        }
      ],
      Bold,
      Link,
      Code,
      HorizontalRule,
      Paragraph,
      HardBreak
    ],
    headers: [
      { text: "No", value: "no", sortable: false },
      {
        text: 'Type',
        value: 'type',
      },
      {
        text: 'Subject',
        value: 'subject',
      },
      {
        text: 'Created At',
        value: 'created_at',
      },
      {
        text: 'Updated At',
        value: 'updated_at',
      },
      {
        text: 'Action',
        value: 'action',
        sortable: false,
      },
    ],
    types: [
      {
        key: 'user_registration_notification',
        value: 'User Registration Notification',
        contents: [
          'name',
          'email',
          'user_approve_link',
          'user_decline_link'
        ],
        default: `
          <p>Hello,</p>
          <p>We have received a new registration request. Please find the details of the user below:</p>
          <p></p>
          <p>Name: [Name]</p>
          <p>Email: [Email]</p>
          <p></p>
          <p>Please click one of the links below to either approve or decline the request</p>
          <p><a style="text-decoration: none;" href='[Booking Approve Link]'>Approve</a> or <a style="text-decoration: none;" href='[Booking Decline Link]'>Decline</a></p>
        `
      },
      {
        key: 'user_registration_approved',
        value: 'User Registration Approved',
        subject: 'Your IntelliTrac Carpooling Account Details',
        contents: [
          'username',
          'user_first_name',
          'user_last_name',
          'user_email',
          'user_type',
          'user_password',
          'licence_type',
          'licence_no',
          'licence_expiry_date',
          'licence_issuing_state'
        ],
        default: `
          <p>Dear [User First Name],</p>
          <p></p>
          <p>Thank you for registering with IntelliTrac Carpooling.</i>
          <p>Here are your temporary login credentials:</i>
          <p></p>
          <p>Username: [Username]</p>
          <p>Password: [Password]</p>
          <p></p>
          <p>For security reasons, please log in to the Carpooling website and update your password as soon as possible.</p>
          <p><a style="text-decoration: none;" href='${process.env.VUE_APP_CARPOOLING_URL}'>Click here to log in<a/></p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'user_registration_declined',
        value: 'User Registration Declined',
        subject: 'IntelliTrac Carpooling Registration Declined',
        contents: [
          'username',
          'user_first_name',
          'user_last_name',
          'user_email',
          'user_type',
          'user_licence_type',
          'licence_no',
          'licence_expiry_date',
          'licence_issuing_state',
        ],
        default: `
          <p>Dear [User First Name],</p>
          <p></p>
          <p>Thank you for your interest in IntelliTrac Carpooling. Unfortunately, your registration request has been declined.</p>
          <p>For further information or to discuss your eligibility, please contact your manager or carpool administrator.</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'booking_notification_email',
        value: 'Booking Notification Email',
        subject: 'Carpool Booking - [Vehicle Make] [Vehicle Model] [Vehicle Plate]',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'vehicle_parking_location',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'cabinet_name',
          'cabinet_key_number',
        ],
        default: `
          <p>Hello,</p>
          <p></p>
          <p>You have booked [Vehicle Name] from [Booking Start Date] until [Booking End Date].</p>
          <p>The vehicle you have booked is parked at [Vehicle Parking Location].</p>
          <p>You can access the keys for the vehicle from [Cabinet Name] the key cabinet in spot number [Cabinet Key Number].</p>
          <p></p>
          <p>Thank you</p>
        `
      },
      {
        key: 'booking_notification_email_without_cabinet',
        value: 'Booking Notification Email without Cabinet',
        subject: 'Carpool Booking - [Vehicle Make] [Vehicle Model] [Vehicle Plate]',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'vehicle_parking_location',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_id',
          'booking_type',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'cabinet_name',
          'cabinet_key_number',
        ],
        default: `
          <p>Hello,</p>
          <p></p>
          <p>You have booked [Vehicle Name] from [Booking Start Date] until [Booking End Date].</p>
          <p>The vehicle you have booked is parked at [Vehicle Parking Location].</p>
          <p></p>
          <p>Thank you</p>
        `
      },
      {
        key: 'booking_out_of_working_hours_request',
        value: 'Booking Out Of Working Hours Request',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_approve_link',
          'booking_decline_link',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Hello,</p>
          <p>[Booking Passenger Name] has requested for out of working hours booking:</p>
          <p></p>
          <p>From: [Booking Origin]</p>
          <p>To: [Booking Destination]</p>
          <p></p>
          <p>[Vehicle Group]</p>
          <p>[Vehicle Name]</p>
          <p>[Booking Passenger Count] Passenger(s)</p>
          <p></p>
          <p>Booking Type: [Booking Type]</p>
          <p>Booking Date: [Booking Period]</p>
          <p></p>
          <p>Please click one of the links below to either approve or decline the request</p>
          <p><a style="text-decoration: none;" href='[Booking Approve Link]'>Approve</a> or <a style="text-decoration: none;" href='[Booking Decline Link]'>Decline</a></p>
        `
      },
      {
        key: 'overnight_booking_request',
        value: 'Overnight Booking Request',
        subject: 'Action Required: Overnight Pool Car Booking Request Approval Required',
        contents: [
          'admin_name',
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_approve_link',
          'booking_decline_link',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Dear [Admin Name],</p>
          <p></p>
          <p>[Booking Passenger Name] has submitted a request for an overnight pool car booking. Below are the details:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Vehicle Group:</b> [Vehicle Group]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Type:</b> [Booking Type]</li>
          <li><b>Booking Date:</b> [Booking Period]</li>
          </ul>
          <p></p>
          <p>Please review the request and take action by clicking one of the links below:</p>
          <a style="text-decoration: none;" href='[Booking Approve Link]'>Approve Booking</a>
          <br>
          <a style="text-decoration: none;" href='[Booking Decline Link]'>Decline Booking</a>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'overnight_booking_request_approved',
        value: 'Overnight Booking Request Approved',
        subject: 'Your Overnight Pool Car Booking Has Been Approved',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>Your request for an overnight pool car booking has been approved. Below are the details:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Vehicle Group:</b> [Vehicle Group]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Type:</b> [Booking Type]</li>
          <li><b>Booking Date:</b> [Booking Period]</li>
          </ul>
          <p></p>
          <p>Please ensure you adhere to company policies regarding vehicle use. If you have any questions or need further assistance, feel free to reach out to your carpool booking administrator.</p>
          <p></p>
          <p>Safe travels!</p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'overnight_booking_request_declined',
        value: 'Overnight Booking Request Declined',
        subject: 'Your Overnight Pool Car Booking Has Been Declined',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>Unfortunately, your request for an overnight pool car booking has been declined. Below are the details of your request:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Vehicle Group:</b> [Vehicle Group]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Type:</b> [Booking Type]</li>
          <li><b>Booking Date:</b> [Booking Period]</li>
          </ul>
          <p></p>
          <p>For more information or to discuss alternative arrangements, please contact your manager or carpool administrator.</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'advance_booking_request',
        value: 'Advance Booking Request',
        subject: 'Action Required: Advance Pool Car Booking Request Approval Required',
        contents: [
          'admin_name',
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_approve_link',
          'booking_decline_link',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Dear [Admin Name],</p>
          <p></p>
          <p>[Booking Passenger Name] has submitted a request for a pool car booking further in advance than permitted. Below are the details:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Vehicle Group:</b> [Vehicle Group]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Type:</b> [Booking Type]</li>
          <li><b>Booking Date:</b> [Booking Period]</li>
          </ul>
          <p></p>
          <p>Please review the request and take action by clicking one of the links below:</p>
          <a style="text-decoration: none;" href='[Booking Approve Link]'>Approve Booking</a>
          <br>
          <a style="text-decoration: none;" href='[Booking Decline Link]'>Decline Booking</a>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'advance_booking_request_approved',
        value: 'Advance Booking Request Approved',
        subject: 'Your Advance Pool Car Booking Has Been Approved',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>Your request for an advance pool car booking has been approved. Below are the details:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Vehicle Group:</b> [Vehicle Group]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Type:</b> [Booking Type]</li>
          <li><b>Booking Date:</b> [Booking Period]</li>
          </ul>
          <p></p>
          <p>Please ensure you adhere to company policies regarding vehicle use. If you have any questions or need further assistance, feel free to reach out to your carpool booking administrator.</p>
          <p></p>
          <p>Safe travels!</p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'advance_booking_request_declined',
        value: 'Advance Booking Request Declined',
        subject: 'Your Advance Pool Car Booking Has Been Declined',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_declined_reason',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>Unfortunately, your request for an advance pool car booking has been declined. Below are the details of your request:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Vehicle Group:</b> [Vehicle Group]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Type:</b> [Booking Type]</li>
          <li><b>Booking Date:</b> [Booking Period]</li>
          <li><b>Reason:</b> [Booking Declined Reason]</li>
          </ul>
          <p></p>
          <p>For more information or to discuss alternative arrangements, please contact your manager or carpool administrator.</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'booking_recurring_request',
        value: 'Recurring Booking Request',
        subject: 'Action Required: Recurring Carpool Booking Request',
        contents: [
          'admin_name',
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_recurring_schedules',
          'booking_approve_link',
          'booking_decline_link',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Dear [Admin Name],</p>
          <p></p>
          <p>[Booking Passenger Name] has submitted a request for a recurring carpool booking. Below are the details:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Vehicle Group:</b> [Vehicle Group]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Type:</b> Recurring</li>
          <li><b>Frequency:</b> [Booking Type]</li>
          <li><b>Start Date:</b> [Booking Start Date]</li>
          <li><b>End Date:</b> [Booking End Date]</li>
          </ul>
          <p></p>
          <p>Please review the request and take action by clicking one of the links below:</p>
          <a style="text-decoration: none;" href='[Booking Approve Link]'>Approve Booking</a>
          <br>
          <a style="text-decoration: none;" href='[Booking Decline Link]'>Decline Booking</a>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'booking_recurring_request_approved',
        value: 'Recurring Booking Request Approved',
        subject: 'Your Recurring Carpool Booking Has Been Approved',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'vehicle_parking_location',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_recurring_schedules',
          'cabinet_name',
          'cabinet_key_number',
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>Your request for a recurring carpool booking has been approved. Below are the details:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Vehicle Group:</b> [Vehicle Group]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Type:</b> Recurring</li>
          <li><b>Frequency:</b> [Booking Type]</li>
          <li><b>Start Date:</b> [Booking Start Date]</li>
          <li><b>End Date:</b> [Booking End Date]</li>
          </ul>
          <p></p>
          <p>Please ensure you adhere to company policies regarding vehicle use. If you have any questions or need further assistance, feel free to reach out to your carpool booking administrator.</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'booking_recurring_request_declined',
        value: 'Recurring Booking Request Declined',
        subject: 'Your Recurring Carpool Booking Has Been Declined',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_recurring_schedules',
          'booking_declined_reason',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>Unfortunately, your request for a recurring carpool booking has been declined. Below are the details of your request:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Vehicle Group:</b> [Vehicle Group]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Type:</b> Recurring</li>
          <li><b>Frequency:</b> [Booking Type]</li>
          <li><b>Start Date:</b> [Booking Start Date]</li>
          <li><b>End Date:</b> [Booking End Date]</li>
          </ul>
          <p></p>
          <pFor further information or to discuss alternative arrangements, please contact your manager or carpool administrator.</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'booking_sharing_ride_request',
        value: 'Rideshare Request',
        subject: 'RideShare Request from [Booking Passenger Name]',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_owner_name',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_approve_link',
          'booking_decline_link',
        ],
        default: `
          <p>Dear [Booking Owner Name],</p>
          <p></p>
          <p>[Booking Passenger Name] has requested to join your ride. Below are the details:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Date:</b> [Booking Period]</li>
          </ul>
          <p></p>
          <p>Please review the request and confirm whether you can accommodate the passenger.</p>
          <a style="text-decoration: none;" href='[Booking Approve Link]'>Approve RideShare</a>
          <br>
          <a style="text-decoration: none;" href='[Booking Decline Link]'>Decline RideShare</a>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'booking_sharing_ride_request_approved',
        value: 'Rideshare Request Approved',
        subject: 'Your RideShare Request Has Been Approved',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'vehicle_parking_location',
          'booking_type',
          'booking_owner_name',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>Great news! [Booking Owner Name] has approved your request to join their rideshare. Below are the details of your ride:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Driver:</b> [Booking Owner Name]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Date:</b> [Booking Period]</li>
          </ul>
          <p></p>
          <p>Please coordinate with [Booking Owner Name] if needed and ensure you arrive on time. If you have any questions or need to make changes, feel free to reach out to [Booking Owner Name].</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'booking_sharing_ride_request_declined',
        value: 'Rideshare Request Declined',
        subject: 'Your RideShare Request Has Been Declined',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_type',
          'booking_owner_name',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'booking_declined_reason'
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>Unfortunately, your request to join a rideshare with [Booking Owner Name] has been declined. Below are the details of your request:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Driver:</b> [Booking Owner Name]</li>
          <li><b>Booking Date:</b> [Booking Period]</li>
          </ul>
          <p></p>
          <p>You may consider requesting another rideshare or exploring alternative travel arrangements. If you have any questions, feel free to reach out.</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'late_returning_key',
        value: 'Late Vehicle Return Notification',
        subject: 'Action Required: Vehicle Return Delay',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'booking_id',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'cabinet_name',
          'cabinet_key_number'
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>Our records indicate that your scheduled return time for the vehicle [Vehicle Name] has passed. If you are experiencing delays, please take one of the following actions:</p>
          <p></p>
          <p>1. Return the vehicle as soon as possible.</p>
          <p>2. Extend your booking if you require the vehicle for a longer period.</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>Current Return Time:</b> [Booking End Date]</li>
          <li><b>Vehicle Name:</b> [Vehicle Name]</li>
          <li><b>Booking Reference:</b> [Booking ID]</li>
          </ul>
          <p></p>
          <p>If you need assistance or have any issues, please contact the carpool administrator as soon as possible.</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'key_pickup_reminder',
        value: 'Key Pickup Reminder',
        subject: 'Reminder: Vehicle Key Collection for Your Carpool Booking',
        contents: [
          'user_first_name',
          'user_last_name',
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'vehicle_parking_location',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_id',
          'booking_type',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period',
          'cabinet_name',
          'cabinet_key_number',
        ],
        default: `
          <p>Dear [User First Name],</p>
          <p>We hope this message finds you well.</p>
          <p>This is a gentle reminder that your carpool booking for <strong>[Vehicle Name]</strong> was scheduled to start at <strong>[Booking Start Date]</strong>. Our records indicate that the vehicle key has not yet been collected from the cabinet.</p>
          <p>To ensure a smooth experience, we kindly request that you collect the key at your earliest convenience.</p>
          <p>Should you no longer require the vehicle, we would appreciate it if you could cancel your booking so that it may be made available for other users.</p>
          <p>If you have any questions or require assistance, please do not hesitate to reach out to us.</p>
          <p>Kind regards,<br>IntelliTrac Carpooling Team</p>
        `
      },
      {
        key: 'swap_vehicle',
        value: 'Vehicle Swap Notification',
        subject: 'Vehicle Swap Notification for Your Booking',
        contents: [
          'vehicle_id',
          'vehicle_name',
          'vehicle_plate',
          'vehicle_make',
          'vehicle_model',
          'vehicle_group',
          'old_vehicle_id',
          'old_vehicle_name',
          'old_vehicle_plate',
          'old_vehicle_make',
          'old_vehicle_model',
          'old_vehicle_group',
          'booking_type',
          'booking_passenger_name',
          'booking_passenger_email',
          'booking_passenger_count',
          'booking_origin',
          'booking_destination',
          'booking_start_date',
          'booking_end_date',
          'booking_period'
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>We want to inform you that your booked vehicle has been swapped due to service or other requirements. Please find the updated details below:</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>From:</b> [Booking Origin]</li>
          <li><b>To:</b> [Booking Destination]</li>
          <li><b>Previous Vehicle:</b> [Old Vehicle Name] ([Old Vehicle Group])</li>
          <li><b>New Vehicle:</b> [Vehicle Name] ([Vehicle Group])</li>
          <li><b>Passengers:</b> [Booking Passenger Count]</li>
          <li><b>Booking Type:</b> [Booking Type]</li>
          <li><b>Passengers:</b> [Booking Period]</li>
          </ul>
          <p></p>
          <p>Please ensure you collect the updated vehicle for your trip. If you have any questions or concerns, feel free to reach out.</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'user_licence_expired',
        value: 'Driver\'s Licence Expiry Notification',
        subject: 'Action Required: Update Your Driver\'s Licence Details',
        contents: [
          'user_name',
          'licence_no',
          'licence_expiry_date',
          'admin_name',
          'council_name',
          'email_address',
          'phone_number',
        ],
        default: `
          <p>Dear [Booking Passenger Name],</p>
          <p></p>
          <p>Our records indicate that your driver\'s licence is nearing its expiry date. To continue using IntelliTrac Carpooling services without interruption, please update your licence details as soon as possible.</p>
          <p></p>
          <ul style='list-style: none;'>
          <li><b>Current Licence Expiry Date:</b> [Licence Expiry Date]</li>
          <li><b>Driver\'s Licence Number:</b> [Licence No]</li>
          </ul>
          <p></p>
          <p>Please ensure that you upload the updated details before the expiry date to maintain compliance with our policies. If you have already renewed your licence, kindly update the information using the link above.</p>
          <p>If you have any questions or need assistance, feel free to reach out.</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
      {
        key: 'user_registration_invitation',
        value: 'User Registration Invitation',
        subject: 'You\'re Invited to Join IntelliTrac Carpooling',
        contents: [
          'registration_link',
        ],
        default: `
          <p>Hello,</p>
          <p></p>
          <p>You have been invited to join IntelliTrac Carpooling.</p>
          <p></p>
          <p>To create your account, please click the link below:</p>
          <p></p>
          <p><a style="text-decoration: none; background-color: #00ACDC; color: #ffffff; padding: 10px 20px; border-radius: 5px;" href='[Registration Link]'>Register Now</a></p>
          <p></p>
          <p>If the button above does not work, please copy and paste the following link into your browser:</p>
          <p>[Registration Link]</p>
          <p></p>
          <p>If you have any questions, feel free to reach out to your administrator.</p>
          <p></p>
          <p>Best regards,</p>
          <p><b>IntelliTrac Carpooling Team</b></p>
        `
      },
    ],
    contents: [
      {
        key: 'username',
        value: '[Username]'
      },
      {
        key: 'user_first_name',
        value: '[User First Name]'
      },
      {
        key: 'user_last_name',
        value: '[User Last Name]'
      },
      {
        key: 'user_type',
        value: '[User Type]'
      },
      {
        key: 'user_password',
        value: '[User Password]'
      },
      {
        key: 'licence_type',
        value: '[Licence Type]'
      },
      {
        key: 'licence_no',
        value: '[Licence No]'
      },
      {
        key: 'licence_expiry_date',
        value: '[Licence Expiry Date]'
      },
      {
        key: 'licence_issuing_state',
        value: '[Licence Issuing State]'
      },
      {
        key: 'vehicle_id',
        value: '[Vehicle ID]'
      },
      {
        key: 'vehicle_name',
        value: '[Vehicle Name]'
      },
      {
        key: 'vehicle_make',
        value: '[Vehicle Make]'
      },
      {
        key: 'vehicle_model',
        value: '[Vehicle Model]'
      },
      {
        key: 'vehicle_plate',
        value: '[Vehicle Plate]'
      },
      {
        key: 'vehicle_group',
        value: '[Vehicle Group]'
      },
      {
        key: 'vehicle_parking_location',
        value: '[Vehicle Parking Location]'
      },
      {
        key: 'old_vehicle_id',
        value: '[Old Vehicle ID]'
      },
      {
        key: 'old_vehicle_name',
        value: '[Old Vehicle Name]'
      },
      {
        key: 'old_vehicle_make',
        value: '[Old Vehicle Make]'
      },
      {
        key: 'old_vehicle_model',
        value: '[Old Vehicle Model]'
      },
      {
        key: 'old_vehicle_plate',
        value: '[Old Vehicle Plate]'
      },
      {
        key: 'old_vehicle_group',
        value: '[Old Vehicle Group]'
      },
      {
        key: 'booking_id',
        value: '[Booking ID]'
      },
      {
        key: 'booking_type',
        value: '[Booking Type]'
      },
      {
        key: 'booking_owner_name',
        value: '[Booking Owner Name]'
      },
      {
        key: 'booking_passenger_name',
        value: '[Booking Passenger Name]'
      },
      {
        key: 'booking_passenger_email',
        value: '[Booking Passenger Email]'
      },
      {
        key: 'booking_passenger_count',
        value: '[Booking Passenger Count]'
      },
      {
        key: 'booking_origin',
        value: '[Booking Origin]'
      },
      {
        key: 'booking_destination',
        value: '[Booking Destination]'
      },
      {
        key: 'booking_start_date',
        value: '[Booking Start Date]'
      },
      {
        key: 'booking_end_date',
        value: '[Booking End Date]'
      },
      {
        key: 'booking_period',
        value: '[Booking Period]'
      },
      {
        key: 'booking_recurring_schedules',
        value: '[Booking Recurring Schedules]'
      },
      {
        key: 'booking_approve_link',
        value: '[Booking Approve Link]'
      },
      {
        key: 'booking_decline_link',
        value: '[Booking Decline Link]'
      },
      {
        key: 'booking_declined_reason',
        value: '[Booking Declined Reason]'
      },
      {
        key: 'user_name',
        value: '[User Name]'
      },
      {
        key: 'admin_name',
        value: '[Admin Name]'
      },
      {
        key: 'council_name',
        value: '[Council Name]'
      },
      {
        key: 'email_address',
        value: '[Email Address]'
      },
      {
        key: 'phone_number',
        value: '[Phone Number]'
      },
      {
        key: 'cost_code',
        value: '[Booking Cost Code]'
      },
      {
        key: 'client_name',
        value: '[Client Name]'
      },
      {
        key: 'cabinet_name',
        value: '[Cabinet Name]'
      },
      {
        key: 'cabinet_key_number',
        value: '[Cabinet Key Number]'
      },
      {
        key: 'user_approve_link',
        value: '[User Approve Link]'
      },
      {
        key: 'user_decline_link',
        value: '[User Decline Link]'
      },
      {
        key: 'registration_link',
        value: '[Registration Link]'
      },
    ],
    valid: true,
    dialog: false,
    replace: false,
    id: '',
    type: '',
    subject: '',
    body: '',
    searchData: '',
    searchDataTimeout: null,
    loadingUpdate: false,
    loader: null,
    overlay: false,
    footerProps: {
      itemsPerPageOptions: [2, 3, 5, 10, 20, 50, 100, 200, 500]
    },
    paginationOptions: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: []
    }
  }),
  created() {
    this.$store.commit('CLEAR_EMAIL_TEMPLATES');
    this.$store.commit('CLEAR_EMAIL_TEMPLATES_TOTAL');
    this.$store.commit('CLEAR_USED_EMAIL_TEMPLATE_TYPES');
    this.$store.dispatch('LOAD_EMAIL_TEMPLATES', this.paginationOptions);
    this.$store.dispatch('LOAD_EMAIL_TEMPLATES_TOTAL');
    this.$store.dispatch('LOAD_USED_EMAIL_TEMPLATE_TYPES');
    this.overlay = true;
  },  
  mounted() {
    if (this.isDarwin) {
      const typeIndex = this.types.findIndex(t => t.key === 'booking_out_of_working_hours_request');
      this.types[typeIndex].contents.push('cost_code');
      this.types[typeIndex].default = `
        <p>Hello,</p>
        <p>[Booking Passenger Name] has requested for out of working hours booking:</p>
        <p></p>
        <p>From: [Booking Origin]</p>
        <p>To: [Booking Destination]</p>
        <p></p>
        <p>[Vehicle Group]</p>
        <p>[Vehicle Name]</p>
        <p>[Booking Passenger Count] Passenger(s)</p>
        <p></p>
        <p>Booking Type: [Booking Type]</p>
        <p>Booking Date: [Booking Period]</p>
        <p>Booking Cost Code: [Booking Cost Code]</p>
        <p></p>
        <p>Please click one of the links below to either approve or decline the request</p>
        <p><a style="text-decoration: none;" href='[Booking Approve Link]'>Approve</a> or <a style="text-decoration: none;" href='[Booking Decline Link]'>Decline</a></p>
      `;
    }
    if (this.isVictrack) {
      this.types[typeIndex].contents.push('cost_code');
      this.types[typeIndex].contents.push('client_name');
    }
  },
  computed: {
    is_page_table() {
      return this.page === 'table';
    },
    is_page_form() {
      return this.page === 'form';
    },
    title() {
      return this.id === '' ? 'Create New Email Template' : 'Edit Email Template';
    },
    loading() {
      return this.$store.getters['loading'];
    },
    loading_total() {
      return this.$store.getters['loading_total'];
    },
    loading_type() {
      return this.$store.getters['loading_type'];
    },
    loading_email() {
      return this.loading || this.loading_total || this.loading_type;
    },
    query() {
      return this.$store.getters['query'];
    },
    email_templates() {
      return this.$store.getters['email_templates'];
    },
    email_templates_total() {
      return this.$store.getters['email_templates_total'];
    },
    used_email_template_types() {
      return this.$store.getters['used_email_template_types'];
    },
    filter_email_template_types() {
      const filtered_types = this.types.filter(type => {
        if (type.key === 'key_pickup_reminder' && !this.isTasWater && !this.isIntimap) {
          return false;
        }

        const matchItem = this.used_email_template_types.find(t => t === type.key);
        if (this.type !== '') {
          if (this.type === type.key) return true;
        }
        return typeof matchItem === 'undefined';
      });
      return filtered_types.sort((a, b) => {
        if (a.value < b.value) return -1;
        if (a.value > b.value) return 1;
        return 0;
      });
    },
    bodyEmpty() {
      return this.body == '' || this.body == '<p></p>';
    },
    isIntimap() {
      return this.$store.state.clientid === "DSSTWWLLIHH7" || this.$store.state.clientid === "ECZ1PXM4JO68"; 
    },
    isDarwin() {
      return this.$store.state.clientid === "ED73T6OILP78";
    },
    isVictrack() {
      return this.$store.state.clientid === "E4IKWG0LDBM8";
    },
    isTasWater() {
      return this.$store.state.clientid === "GHHHZKDOAAA0";
    }
  },
  watch: {
    type() {
      this.$nextTick(() => {
        if (this.type === '') return
        const toolbar = this.$refs.tiptap.$children[0].$el.querySelector('.tiptap-vuetify-editor__toolbar .v-toolbar__content');
        toolbar.querySelector('.select-option__content').disabled = false;
        this.updateContentList();
      });
    },
    loader() {
      const l = this.loader
      this[l] = !this[l]

      setTimeout(() => (this[l] = false), 3000)

      this.loader = null
    },
    overlay(val) {
      val && setTimeout(() => {
        this.overlay = false
      }, 2e3)
    },
    searchData(query) {
      clearTimeout(this.searchDataTimeout);
      this.searchDataTimeout = setTimeout(() => {
        this.paginationOptions.page     = 1;
        this.paginationOptions.sortBy   = [];
        this.paginationOptions.sortDesc = [];

        this.$store.commit('CLEAR_QUERY');
        this.$store.commit('CLEAR_EMAIL_TEMPLATES');
        this.$store.commit('CLEAR_EMAIL_TEMPLATES_TOTAL');
        this.$store.commit('CLEAR_USED_EMAIL_TEMPLATE_TYPES');
        this.$store.commit('SET_QUERY', query);
        this.$store.dispatch('LOAD_EMAIL_TEMPLATES', this.paginationOptions);
        this.$store.dispatch('LOAD_EMAIL_TEMPLATES_TOTAL');
        this.$store.dispatch('LOAD_USED_EMAIL_TEMPLATE_TYPES');
      }, 1e3);
    }
  },
  methods: {
    formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
      const selectedDate = moment(date);
      if (selectedDate.isValid()) {
        return selectedDate.format(format);
      }
      return date;
    },
    handleClickCreateNew() {
      const currentPage = 'form';
      this.page = currentPage;
      this.createSelectOption();
    },
    handleClickBack() {
      const currentPage = 'table';
      this.page = currentPage;
      this.id = '';
      this.type = '';
      this.subject = '';
      this.body = '';
    },
    handleConfirmDelete() {
      this.dialog = false;
      this.loadingUpdate = true;
      this.loader = this.loadingUpdate;
      this.$store
      .dispatch('DELETE_EMAIL_TEMPLATE', this.id)
      .then(() => {
        this.loadingUpdate = false;
        this.loader = this.loadingUpdate;
        this.$swal({
          icon: "success",
          title: "Success delete email template",
        });
        this.$store.commit('CLEAR_EMAIL_TEMPLATES_TOTAL');
        this.$store.commit('CLEAR_EMAIL_TEMPLATES');
        this.$store.commit('CLEAR_USED_EMAIL_TEMPLATE_TYPES');
        this.$store.dispatch('LOAD_EMAIL_TEMPLATES_TOTAL');
        this.$store.dispatch('LOAD_USED_EMAIL_TEMPLATE_TYPES');
        this.$store
        .dispatch('LOAD_EMAIL_TEMPLATES', this.paginationOptions)
        .then(() => {
          this.id = '';
        });
      })
      .catch((err) => {
        this.loadingUpdate = false;
        this.loader = this.loadingUpdate;
        this.$swal({
          icon: "error",
          title: err.message || 'Failed to delete email template.',
        });
      });
    },
    handleClickDelete(item) {
      this.dialog = true;
      this.id = item.id;
    },
    handleClickEdit(item) {
      const currentPage = 'form';
      this.page = currentPage;
      this.id = item.id;
      this.type = item.type;
      this.subject = item.subject;
      this.body = this.convertToChips(item.body);
      this.createSelectOption();
    },
    handleClickSubmit() {
      if (this.$refs.form.validate() && this.body != '') {
        let action = 'CREATE';
        const payload = {
          type: this.type,
          subject: this.subject,
          body: this.stripChips(this.body),
          active: 1,
        };
        let successMsg = this.id === '' ? 'Email template created successfully.' : 'Email template updated succesfully';

        if (this.id !== '') {
          action = 'UPDATE';
          payload.id = this.id;
        }

        this.loadingUpdate = true;
        this.loader = this.loadingUpdate;

        this.$store
          .dispatch(`${action}_EMAIL_TEMPLATE`, payload)
          .then(() => {
            this.loadingUpdate = false;
            this.loader = this.loadingUpdate;
            this.$swal({
              icon: "success",
              title: successMsg,
            });
            this.$store.commit('CLEAR_USED_EMAIL_TEMPLATE_TYPES');
            this.$store.commit('CLEAR_EMAIL_TEMPLATES_TOTAL');
            this.$store.commit('CLEAR_EMAIL_TEMPLATES');
            this.$store.dispatch('LOAD_USED_EMAIL_TEMPLATE_TYPES');
            this.$store.dispatch('LOAD_EMAIL_TEMPLATES_TOTAL');
            this.$store
              .dispatch('LOAD_EMAIL_TEMPLATES', this.paginationOptions)
              .then(() => {
                this.handleClickBack();
              });
          })
          .catch((err) => {
            this.loadingUpdate = false;
            this.loader = this.loadingUpdate;
            const message = (err.response && err.response.data && err.response.data.data && err.response.data.data.message)
              || err.message
              || 'Failed to save email template.';
            this.$swal({
              icon: "error",
              title: message,
            });
          });
      }
    },
    createSelectOption() {
      this.$nextTick(() => {
        this.$refs.form.validate();

        const vm = this;
        const toolbar = this.$refs.tiptap.$children[0].$el.querySelector('.tiptap-vuetify-editor__toolbar .v-toolbar__content');

        const label = document.createElement('label');
        label.style.marginLeft = '1rem';

        const span = document.createElement('span');
        span.innerHTML = 'Insert Content: ';
        label.appendChild(span);

        const selectOption = document.createElement('select');
        selectOption.classList.add('select-option__content');
        selectOption.style.background = '#fff';

        if (vm.type === '') selectOption.disabled = true;

        label.appendChild(selectOption);
        toolbar.appendChild(label);

        for (const content of this.contents) {
          const option = document.createElement('option');
          option.value = content.key;
          option.innerHTML = content.value;
          selectOption.appendChild(option);
        }

        let savedSelection = null;

        selectOption.addEventListener('mousedown', () => {
          const editor = vm.$refs.tiptap.editor;
          savedSelection = editor.view.state.selection;
        });

        selectOption.addEventListener('change', (e) => {
          const emptyBody = vm.body.trim() === '' || vm.body.trim() === '<p></p>';
          const content = vm.contents.find(content => content.key === e.target.value);

          if (emptyBody) {
            vm.body = `<p><span class="content-placeholder" data-label="${content.value}">${content.value}</span></p>`;
          } else if (savedSelection) {
            const editor = vm.$refs.tiptap.editor;
            const { schema } = editor.view.state;
            const node = schema.nodes.content_placeholder.create({ label: content.value });
            const tr = editor.view.state.tr.replaceWith(savedSelection.from, savedSelection.to, node);
            editor.view.dispatch(tr);
          }

          savedSelection = null;

          vm.$nextTick(() => {
            const editor = vm.$refs.tiptap.editor;
            if (editor && editor.view) editor.view.focus();
          });
        });
      });
    },
    updateContentList() {
      this.$nextTick(() => {
        const type = this.types.find(type => type.key === this.type);
        const toolbar = this.$refs.tiptap.$children[0].$el.querySelector('.tiptap-vuetify-editor__toolbar .v-toolbar__content');
        const contentOptions = toolbar.querySelectorAll('.select-option__content option');

        contentOptions.forEach((item) => {
          if (item.style) {
            item.style.display = 'none';
          }
        });

        let selected = '';

        for (const content of type.contents) {
          const selectedContent = Array.from(contentOptions).find(option => option.value === content);
          if (selected == '') {
            selected = selectedContent;
            toolbar.querySelector('.select-option__content').value = content
          }
          if (selectedContent) {
            selectedContent.style.display = '';
          }
        }
      });
    },
    updateEmailTemplate(paginationOptions) {
      this.paginationOptions = paginationOptions;
      this.$store.commit('CLEAR_EMAIL_TEMPLATES');
      this.$store.commit('CLEAR_EMAIL_TEMPLATES_TOTAL');
      this.$store.commit('CLEAR_USED_EMAIL_TEMPLATE_TYPES');
      this.$store.dispatch('LOAD_EMAIL_TEMPLATES', this.paginationOptions);
      this.$store.dispatch('LOAD_EMAIL_TEMPLATES_TOTAL');
      this.$store.dispatch('LOAD_USED_EMAIL_TEMPLATE_TYPES');
    },
    getTypeName(type) {
      const selectedType = this.types.find(t => t.key == type);
      return selectedType.value;
    },
    handleLoadDefault() {
      if (!this.bodyEmpty) {
        this.replace = true
      } else {
        this.handleClickReplace()
      }
    },
    stripChips(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      div.querySelectorAll('.content-placeholder').forEach(span => {
        span.replaceWith(span.getAttribute('data-label'));
      });
      return div.innerHTML;
    },
    convertToChips(html) {
      const div = document.createElement('div');
      div.innerHTML = html;

      const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT, null, false);
      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) {
        textNodes.push(node);
      }

      textNodes.forEach(textNode => {
        const text = textNode.textContent;
        if (!/\[[^\]]+\]/.test(text)) return;

        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        const regex = /\[[^\]]+\]/g;
        let match;

        while ((match = regex.exec(text)) !== null) {
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
          }
          const span = document.createElement('span');
          span.className = 'content-placeholder';
          span.setAttribute('data-label', match[0]);
          span.setAttribute('contenteditable', 'false');
          span.textContent = match[0];
          fragment.appendChild(span);
          lastIndex = match.index + match[0].length;
        }

        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        textNode.parentNode.replaceChild(fragment, textNode);
      });

      return div.innerHTML;
    },
    handleClickReplace() {
      const selectedType = this.types.find(type => type.key === this.type)
      this.body = this.convertToChips(selectedType.default)
      this.subject = selectedType.subject ? selectedType.subject : ''
      this.replace = false
    }
  }
}
</script>

<style>
.content-placeholder {
  display: inline-block;
  background: #e3f2fd;
  color: #00ACDC;
  border: 1px solid #00ACDC;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 0.875em;
  font-weight: 500;
  white-space: nowrap;
  cursor: default;
  user-select: all;
  line-height: 1.4;
}

.ProseMirror .content-placeholder.ProseMirror-selectednode {
  outline: 2px solid #00ACDC;
  outline-offset: 1px;
}
</style>
